import { createSelector } from 'reselect';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type {
    StateTree,
    ProgramQuote,
    RetainedProgramDisplay,
    PendingApplyProgram,
    ProgramIncentivesMeta
} from '@makemydeal/dr-dash-types';
import type { LenderGuideLineItem } from '../types/guidelineAlertsTypes';
import type {
    ProgramsState,
    Incentive,
    Exclusions,
    AppliedPaymentBaseline,
    ProgramFallbackHold,
    TermIncentiveMeta
} from '@makemydeal/dr-dash-types';
import type { IResponsePayment, OfferType, PaymentTerm } from '@makemydeal/dr-platform-types';
import { CASH, FINANCE } from '@makemydeal/dr-platform-types';

type TermWithIncentivesContext = PaymentTerm & { incentivesContext?: ProgramIncentivesMeta };
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { incentiveSelectors, utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';

import { buildIncentivesContextId, describesSameLenderProgram, mapPaymentResponseToProgramQuote } from '../utils/programUtils';
import { formatLenderProgramDisplay } from '@makemydeal/dr-shared-ui-utils';

import * as offerRedux from './offerRedux';
import * as vehicleSelectors from './vehicleSelectors';
import { getResolvedAutoSelectChecked } from './manualFieldOverridesSelectors';
import { KEEP_LENDER_META } from '../programInvalidatingActions';
import type { KeptLender } from '../programInvalidatingActions';

export const getPrograms = (state: any): ProgramsState => state?.programs || {};

const isRestorationEnabled = (state: StateTree): boolean => offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state);

// Gates every DE759607/US2076398 cash-deal-incentives fix in this file. See the toggle's selector
// definition for the full list of consumers this feature spans.
const isCashFixesEnabled = (state: StateTree): boolean => offerReduxUtils.incentives.isCashDealIncentivesFixesEnabled(state);

export const isExplicitLenderP1Active = (state: StateTree): boolean =>
    featureToggleSelectors.areFeatureTogglesAvailable(state) && featureToggleSelectors.isExplicitLenderP1Enabled(state);

export const explicitLenderP1AppliesToOffer = (state: StateTree, offerType: OfferType): boolean =>
    isExplicitLenderP1Active(state) && offerType !== CASH;

export const autoSelectedBannerAppliesToOffer = (state: StateTree, offerType: OfferType): boolean =>
    explicitLenderP1AppliesToOffer(state, offerType) &&
    featureToggleSelectors.isAutoSelectedLenderProgramBannerEnabled(state) &&
    (offerType !== FINANCE || !offerRedux.isFlexBuyPayment(state));

const isLockableProgram = (program: unknown): boolean =>
    offerReduxUtils.isValidProgram(program) && !offerReduxUtils.isUserPricedProgram(program);

const withLenderCode = (program: Record<string, any>, lenderCode?: string): Record<string, any> =>
    lenderCode != null ? { ...program, lenderCode } : program;

const resolveExplicitDealProgram = (
    state: StateTree,
    offerType: OfferType
): { program: Record<string, any>; lenderCode?: string } | undefined => {
    const cachedProgram = offerRedux.getPaymentProgramInfoByTerm(state, offerType);
    const cached = isLockableProgram(cachedProgram) ? cachedProgram : undefined;
    const selectedTerm = offerRedux.getSelectedTermByOfferType(state, offerType);
    const term = selectedTerm && isLockableProgram(selectedTerm.lenderProgram) ? selectedTerm : undefined;
    const latchedProgram = getPrograms(state).retainedProgramDisplay?.[offerType]?.program;
    const latched = latchedProgram && isLockableProgram(latchedProgram.lenderProgram) ? latchedProgram : undefined;

    if (latched) {
        if (cached && describesSameLenderProgram(cached, latched.lenderProgram)) {
            return { program: cached, lenderCode: latched.lenderCode };
        }
        if (term && describesSameLenderProgram(term.lenderProgram, latched.lenderProgram)) {
            return {
                program: offerReduxUtils.buildAppliedProgramInfo(undefined, term),
                lenderCode: latched.lenderCode ?? term.lenderCode
            };
        }
        return { program: offerReduxUtils.buildAppliedProgramInfo(undefined, latched), lenderCode: latched.lenderCode };
    }

    if (term) {
        const program =
            cached && describesSameLenderProgram(cached, term.lenderProgram)
                ? cached
                : offerReduxUtils.buildAppliedProgramInfo(undefined, term);
        return { program, lenderCode: term.lenderCode };
    }

    return cached ? { program: cached } : undefined;
};

export const getExplicitDealProgram = (state: StateTree, offerType: OfferType): Record<string, any> | undefined => {
    if (!explicitLenderP1AppliesToOffer(state, offerType)) return undefined;
    const resolved = resolveExplicitDealProgram(state, offerType);
    return resolved && withLenderCode(resolved.program, resolved.lenderCode);
};

/**
 * On Programs & Incentives close, the manual-select cleanup in `ProgramsAndIncentivesCompactUI`
 * defaults to forcing the deal back onto UPQ "to uncheck auto-select program" - correct for a dealer who
 * has Auto Select Program ON, but for a dealer who already opted out (checkbox already unchecked, nothing
 * to uncheck) it just discards the program they picked. This mirrors `dealerOptOutBlocksPqExcursion`
 * (programActionCreators.ts) but is intentionally its own toggle rather than reusing the existing
 * list-select bypass's toggle, so this close-time behavior can ship/roll back independently.
 *
 * Reads `state.featureToggles` directly rather than `featureToggleSelectors.getFeatureToggle` - same
 * reason as `dealerOptOutBlocksPqExcursion` (programActionCreators.ts): `getFeatureToggle` throws unless
 * the app has bootstrapped (`areFeatureTogglesAvailable`), and this selector's own spec exercises it against
 * minimal state that never bootstraps. `StateTree` doesn't declare `featureToggles`, so the parameter is typed `any` here
 * instead of casting the property access, matching that same file's sibling predicates.
 */
export const dealerOptOutUpqBypassAppliesToOffer = (state: any, offerType: OfferType): boolean =>
    offerType !== CASH &&
    Boolean(state.featureToggles?.DE774140_Bypass_Manual_Select_Upq_Restore) &&
    offerRedux.getDealerOptedOutOfAutoSelectProgram(state) &&
    !getResolvedAutoSelectChecked(state, offerType);

export const getHasSelectedProgram = (state: StateTree): boolean => {
    if (!isRestorationEnabled(state)) return Boolean(getPrograms(state).selectedProgram);
    const offerType = offerRedux.getCurrentOfferType(state);
    return Boolean(offerRedux.getSelectedTermByOfferType(state, offerType)?.lenderProgram?.programName);
};

export const getSelectedProgram = (state: StateTree): ProgramQuote | undefined => {
    if (!isRestorationEnabled(state)) return getPrograms(state).selectedProgram;
    return getHasSelectedProgram(state) ? getProgramFromCurrentOffer(state) : undefined;
};

export const getIsLenderProgramAutoSelected = (state: StateTree): boolean => {
    const offerType = offerRedux.getCurrentOfferType(state);
    return (
        autoSelectedBannerAppliesToOffer(state, offerType) &&
        dealerSelectors.isDealCentralExperience(state) &&
        !offerRedux.getCreditDecisionEnabled(state) &&
        describesSameLenderProgram(
            offerRedux.getSelectedTermByOfferType(state, offerType)?.lenderProgram,
            getPrograms(state).autoSelectedProgramByOfferType?.[offerType]
        )
    );
};

export const getKeptLenderToReapply = (action: AnyFSA, state: StateTree): KeptLender | undefined => {
    const keptLender = action.meta?.originalAction?.meta?.[KEEP_LENDER_META];
    const programFound = Boolean(offerRedux.getSelectedTerm(state)?.lenderProgram?.programName);
    return keptLender && !action.meta.isStalePaymentResponse && programFound ? keptLender : undefined;
};

export const getProgramLossDisabledIds = (state: StateTree): number[] => getPrograms(state).programLossDisabledIds ?? [];

export const getSelectedTermIncentivesContext = (state: StateTree): ProgramIncentivesMeta | undefined =>
    (offerRedux.getSelectedTerm(state) as TermWithIncentivesContext | undefined)?.incentivesContext;

export const getIncentivesInputsFingerprint = (state: StateTree): string => {
    const { chromeStyleId } = vehicleSelectors.getVehicle(state);
    const vin = vehicleSelectors.getVehicleVin(state);
    const condition = vehicleSelectors.getVehicleCondition(state);
    const zipCode = offerRedux.getShopperInfo(state)?.zip ?? dealerSelectors.getDealerZip(state);
    const dealerId = dealerSelectors.getDealerId(state);
    return [chromeStyleId, vin, condition, zipCode, dealerId].map((value) => value ?? '').join('|');
};

export const areIncentivesLoadedForProgram = (
    state: StateTree,
    offerType: OfferType,
    program: ProgramQuote | undefined
): boolean => {
    if (program == null) return false;
    const context = (offerRedux.getSelectedTerm(state, offerType) as TermWithIncentivesContext | undefined)?.incentivesContext;
    const incentivesContextId = buildIncentivesContextId(program);
    return (
        context != null && incentivesContextId != null && context.programContextId?.startsWith(`${incentivesContextId}|`) === true
    );
};

/**
 * This utility is used to determine the incentive amounts for a given term and combine them with the main incentive for rendering.
 */
export const getIncentivesWithDeterminedAmountsByTerm = (incentives: Incentive[], term: number) => {
    const amounts = incentives.map((i) => offerReduxUtils.incentives.transformTermIncentive(term, i));
    return incentives.map((incentive, index) => ({ ...incentive, cash: amounts[index].amount } as Incentive));
};

// Cash has no term to read incentivesContext from (getSelectedTerm(state, CASH) is always {}), so
// compatibleIncentiveIds for cash is carried on byOfferType.cash.incentivesContext instead - read
// from there for cash rather than the always-empty term.
export const getSelectedTermCompatibleIncentiveIds = (state: StateTree): number[] | undefined => {
    if (offerRedux.getCurrentOfferType(state) === CASH && isCashFixesEnabled(state)) {
        return offerRedux.getCashIncentivesContext(state as any)?.compatibleIncentiveIds;
    }
    return getSelectedTermIncentivesContext(state)?.compatibleIncentiveIds;
};

const getGatedAppliedIncentiveIds = (state: StateTree): number[] | null =>
    offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerRedux.getCurrentOfferType(state))
        ? getAppliedIncentiveIdsForCurrentOffer(state)
        : null;

const keepCompatibleOrApplied = (
    incentives: Incentive[],
    compatibleIncentiveIds: number[] | undefined,
    appliedIds: number[] | null
): Incentive[] => {
    if (compatibleIncentiveIds == null || compatibleIncentiveIds.length === 0) {
        return incentives;
    }
    const kept = new Set([...compatibleIncentiveIds, ...(appliedIds ?? [])]);
    return incentives.filter((incentive) => kept.has(incentive.incentiveId));
};

export const getAvailableConditionalIncentives = createSelector(
    [
        (state: StateTree) => getConditionalIncentivesFromSelectedTerm(state),
        (state: StateTree) => getSelectedTermCompatibleIncentiveIds(state),
        getGatedAppliedIncentiveIds
    ],
    keepCompatibleOrApplied
);

export const getAppliedNonConditionalIncentives = (state: StateTree): Incentive[] =>
    getNonConditionalIncentivesFromSelectedTerm(state);

export const getAvailableNonConditionalIncentives = createSelector(
    [
        (state: StateTree) => getNonConditionalIncentivesFromSelectedTerm(state),
        (state: StateTree) => getSelectedTermCompatibleIncentiveIds(state),
        getGatedAppliedIncentiveIds
    ],
    keepCompatibleOrApplied
);

// Hand-rolled single-slot cache rather than reselect's createSelector: reselect v5 also memoizes on
// the raw state argument, so two calls with the same state reference short-circuit to the previous
// result even if the derived value differs - this file's tests call selectors repeatedly against one
// shared state literal while re-mocking dependencies, which that args-level shortcut breaks.
let cachedCashAppliedIncentiveIdsSource: unknown;
let cachedCashAppliedIncentiveIds: number[] = Object.freeze([]) as any;

const getCashAppliedIncentiveIds = (state: StateTree): number[] => {
    const source = offerRedux.getOfferDetails(state, CASH)?.appliedIncentiveIds;
    if (source !== cachedCashAppliedIncentiveIdsSource) {
        cachedCashAppliedIncentiveIdsSource = source;
        // Frozen so a caller mutating what it read (all in-repo today, but nothing enforces that)
        // can't corrupt the cached value for the next caller until the source actually changes.
        cachedCashAppliedIncentiveIds = Object.freeze(
            ((source ?? []) as unknown[]).filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
        ) as any;
    }
    return cachedCashAppliedIncentiveIds;
};

let lastEditedFilter: { ids: number[]; edited: string; result: number[] } | undefined;

const withoutEditedIncentiveIds = (state: StateTree, offerType: OfferType, ids: number[]): number[] => {
    if (!offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType)) {
        return ids;
    }
    const edited = incentiveSelectors.getEditedIncentiveIds(state, offerType);
    if (!ids.some((id) => edited.has(id))) {
        return ids;
    }
    const editedKey = [...edited].sort().join(',');
    if (lastEditedFilter?.ids === ids && lastEditedFilter.edited === editedKey) {
        return lastEditedFilter.result;
    }
    const result = ids.filter((id) => !edited.has(id));
    lastEditedFilter = { ids, edited: editedKey, result };
    return result;
};

// offer-redux's getAppliedIncentiveIdsForSelectedTerm is shared with other applications (e.g. Sales
// View) that deliberately exclude cash from its penciled-inputs fallback for reasons specific to
// their own flow, so it can't be changed there. Cash has no term, but byOfferType.cash.appliedIncentiveIds
// is refreshed from the orchestrator response on every payment recalc and is always authoritative for
// Manager View - read it directly instead of the always-empty term-based result for cash. See DE759607.
export const getAppliedIncentiveIdsForCurrentOffer = (state: StateTree, offerType?: OfferType): number[] => {
    const resolvedOfferType = offerType ?? offerRedux.getCurrentOfferType(state);
    if (resolvedOfferType === CASH && isCashFixesEnabled(state)) {
        return getCashAppliedIncentiveIds(state);
    }
    return withoutEditedIncentiveIds(
        state,
        resolvedOfferType,
        offerRedux.getAppliedIncentiveIdsForSelectedTerm(state, resolvedOfferType)
    );
};

export const getAppliedConditionalIncentives = (state: StateTree): Incentive[] => {
    const appliedIds = new Set(getAppliedIncentiveIdsForCurrentOffer(state));
    return getConditionalIncentivesFromSelectedTerm(state).filter(({ incentiveId }) => appliedIds.has(incentiveId));
};

export const sumAllAppliedIncentives = (state: StateTree): number => {
    const incentives = [...getAppliedNonConditionalIncentives(state), ...getAppliedConditionalIncentives(state)];
    return incentives.map((i) => i.cash).reduce((prev, cur) => prev + cur, 0);
};

export const sumConditionalAppliedIncentives = (state: StateTree): number => {
    const incentives = [...getAppliedConditionalIncentives(state)];
    return incentives.map((i) => i.cash).reduce((prev, cur) => prev + cur, 0);
};

const EMPTY_PROGRAMS: readonly IResponsePayment[] = Object.freeze([]);

export const getActivePrograms = (state: StateTree): readonly IResponsePayment[] => getPrograms(state).programs ?? EMPTY_PROGRAMS;

export const getAllMessages = (state: StateTree): LenderGuideLineItem[] => {
    const programs = getActivePrograms(state);

    const alerts = programs.reduce((acc: LenderGuideLineItem[], program) => {
        if (Array.isArray(program.messages) && program.messages.length > 0) {
            const combinedMessage = program.messages.map((msgObj: any) => msgObj.message).join(', ');
            acc.push({
                message: combinedMessage,
                lenderCode: program.lenderCode as string
            });
        }
        return acc;
    }, []);

    return alerts;
};

export const getLenderIdFromSelected = (state: StateTree): number | undefined => getSelectedProgram(state)?.lenderProgram?.lenderId;

export const getIncentivesLoadingError = (state: StateTree): boolean => {
    return getPrograms(state).incentivesError ? true : false;
};

export const getProgramsLoadingError = (state: StateTree): boolean => {
    return getPrograms(state).programsError ? true : false;
};

export const getProgramsLoadingStatus = (state: StateTree): boolean => {
    return getProgramsLoadingError(state) || getPrograms(state).programs != null ? false : true;
};

export const getSelectedProgramIncentives = (state: StateTree): number[] => {
    return getSelectedProgram(state)?.appliedIncentiveIds || [];
};

export const getSelectedIncentives = (state: StateTree): number[] => {
    if (isRestorationEnabled(state)) {
        return offerRedux.getSelectedIncentiveIdsFromPenciledInputs(state);
    }
    return state.programs?.selectedIncentives || [];
};

export const getSelectedProgramTerm = (state: StateTree): number | undefined => {
    return getSelectedProgram(state)?.term;
};

/**
 * Check if incentive has been selected in the current state.
 * @param incentiveId Incentive ID provided by PS
 * @returns A selector that evaluates if this incentive is selected.
 */
export const isIncentiveSelectedSelector = (incentiveId: number) => (state: StateTree) => {
    const selectedIncentives = getSelectedIncentives(state);
    return selectedIncentives.indexOf(incentiveId) >= 0;
};

/**
 * Builds the conditional incentive selection list (the Active Programs checkbox rows) from the
 * selected term's incentives — the durable source — instead of the transient programs.available.
 */
const toIncentiveFromTerm = (incentive: any, incentiveType?: string, programId?: string): Incentive =>
    ({
        incentiveId: offerReduxUtils.incentives.parseIncentiveId(incentive.id),
        cash: incentive.amount,
        programName: incentive.name,
        programId: programId ?? incentive.programId,
        isStandardIncentive: incentive.isStandardIncentive,
        incentiveType: incentiveType ?? incentive.incentiveType,
        exclusions: incentive.exclusions ?? [],
        description: incentive.description
    } as Incentive);

// Cash deals have no term (getSelectedTerm(state, CASH) is always {}), so program-orchestrator
// incentives for cash are written to byOfferType.cash.incentives instead of a term - read from
// there for cash rather than from the (always empty) selected term. See DE759607.
// getCashConditionalsIncentives/getCashNonConditionalsIncentives are typed as taking the Offer
// slice, but (like getOffer/getPenciledInputs) resolve it defensively from either the offer slice
// or the full state via getStateSlice, so the full StateTree works fine here at runtime.
const getCashIncentivesBucket = (state: StateTree, bucket: 'conditionals' | 'nonConditionals') =>
    bucket === 'conditionals'
        ? offerRedux.getCashConditionalsIncentives(state as any)
        : offerRedux.getCashNonConditionalsIncentives(state as any);

const makeTermIncentivesSelector = (bucket: 'conditionals' | 'nonConditionals') =>
    createSelector(
        [
            (state: StateTree) =>
                offerRedux.getCurrentOfferType(state) === CASH && isCashFixesEnabled(state)
                    ? getCashIncentivesBucket(state, bucket)
                    : offerRedux.getSelectedTerm(state)?.incentives?.[bucket],
            (state: StateTree) => offerRedux.getIncentives(state).byId
        ],
        (list, byId) =>
            ((list as any[]) ?? []).map((incentive) => {
                const meta = (byId as Record<string, TermIncentiveMeta>)?.[incentive.id];
                return toIncentiveFromTerm(incentive, meta?.incentiveType, meta?.programId);
            })
    );

export const getConditionalIncentivesFromSelectedTerm = makeTermIncentivesSelector('conditionals');

export const getNonConditionalIncentivesFromSelectedTerm = makeTermIncentivesSelector('nonConditionals');

export const isSelectedTermCatalogUnnamed = (state: StateTree): boolean => {
    if (!offerReduxUtils.incentives.isGridCashDownCatalogCarryEnabled(state) || !isRestorationEnabled(state)) return false;
    if (offerRedux.getCurrentOfferType(state) === CASH) return false;
    const term = offerRedux.getSelectedTerm(state);
    if (!term?.lenderProgram?.programName) return false;
    const catalog = [...(term.incentives?.conditionals ?? []), ...(term.incentives?.nonConditionals ?? [])] as { name?: string }[];
    return catalog.length > 0 && catalog.every((incentive) => !incentive.name);
};

// Cash deals have no term, so both the incentive catalog and the "currently applied" ids that
// decide which catalog entries act as excluders have to come from the cash-specific sources
// instead of the (always empty) selected term. See DE759607.
const getExclusionsFromSelectedTerm = (state: StateTree): Exclusions => {
    const isCash = offerRedux.getCurrentOfferType(state) === CASH && isCashFixesEnabled(state);
    let termIncentives: Array<{ id: string; exclusions?: number[] }>;
    let appliedIds: Set<number>;
    if (isCash) {
        termIncentives = [...getCashIncentivesBucket(state, 'conditionals'), ...getCashIncentivesBucket(state, 'nonConditionals')];
        // Use the same orchestrator-applied source getAppliedIncentiveIdsForCurrentOffer relies on for
        // cash elsewhere in this file, rather than what the user selected - they can disagree when the
        // orchestrator drops an incentive as incompatible, and the exclusion map should reflect what's
        // actually applied.
        appliedIds = new Set<number>(getAppliedIncentiveIdsForCurrentOffer(state, CASH));
    } else {
        const term = offerRedux.getSelectedTerm(state);
        termIncentives = [...(term?.incentives?.conditionals ?? []), ...(term?.incentives?.nonConditionals ?? [])];
        appliedIds = new Set<number>((term?.appliedIncentiveIds ?? []) as number[]);
    }
    const exclusions: Exclusions = {};
    termIncentives.forEach((incentive) => {
        const incentiveId = offerReduxUtils.incentives.parseIncentiveId(incentive.id);
        if (appliedIds.has(incentiveId)) {
            exclusions[incentiveId] = incentive.exclusions ?? [];
        }
    });
    return exclusions;
};

export const getincentiveExclusionsById = (state: StateTree): Exclusions => getExclusionsFromSelectedTerm(state);

export const isIncentiveExcluded =
    (incentiveId: number, isProgramIncentivesRestorationEnabled = false) =>
    (state: StateTree) => {
        const exclusions = getincentiveExclusionsById(state);

        if (!Object.keys(exclusions).length) return false;

        if (isProgramIncentivesRestorationEnabled) {
            const selectedIncentiveIds = getSelectedIncentives(state);
            return Object.entries(exclusions).some(
                ([excluderId, list]) => selectedIncentiveIds.includes(Number(excluderId)) && list.includes(incentiveId)
            );
        }

        return Object.values(exclusions).some((exlusionsById: number[]) => exlusionsById.includes(incentiveId));
    };

export const getProgramFromCurrentOffer = (state: StateTree) => {
    const offerType = offerRedux.getCurrentOfferType(state);

    const term = offerRedux.getSelectedTermByOfferType(state, offerType);
    const appliedProgramInfo = offerRedux.getPaymentProgramInfoByTerm(state, offerType, term?.months);
    const appliedIncentiveIds = offerRedux
        .getAppliedIncentives(state)
        .map((i) => i.code)
        .filter((i) => i != null);
    const temp: any = {
        acqFee: offerRedux.getDealerAcquisitionFeeFromTerm(term)?.dealerFeeAmount,
        appliedIncentiveAmount: offerRedux.getAppliedIncentivesTotalForDisplay(state),
        appliedIncentiveIds: appliedIncentiveIds,
        baseResidualDollar: offerRedux.getBaseResidualDollar(state),
        baseResidualPercent: offerRedux.getBaseResidualPercent(state),
        buyRate: offerRedux.getBuyRateOverride(state, true),
        creditTier: appliedProgramInfo?.creditTier,
        creditTierDescription: term?.creditTierDescription,
        lenderCode: term?.lenderCode,
        lenderLegalName: term?.lenderLegalName,
        lenderProgram: term?.lenderProgram,
        monthlyPayment: term?.monthlyPayment,
        rateType: offerRedux.getRateType(state),
        securityDeposit: offerRedux.getSecurityDepositOverride(state, true),
        sellRate: offerRedux.getSellRateOverride(state, true),
        term: term?.months,
        annualMiles: offerRedux.getAnnualMiles(state),
        profit: term?.profit
    };

    const quote: ProgramQuote = mapPaymentResponseToProgramQuote(temp);
    return quote;
};

const withDisplayFromProgram = (retained?: RetainedProgramDisplay): RetainedProgramDisplay | undefined => {
    const program = retained?.program;
    if (!program) return retained;
    const programName = program.lenderProgram?.programName || retained.programName;
    const lenderLegalName = program.lenderLegalName || retained.lenderLegalName;
    if (programName === retained.programName && lenderLegalName === retained.lenderLegalName) return retained;
    return { ...retained, programName, lenderLegalName };
};

export const getLatchedRetainedProgramDisplay = (state: StateTree): RetainedProgramDisplay | undefined =>
    withDisplayFromProgram(getPrograms(state).retainedProgramDisplay?.[offerRedux.getCurrentOfferType(state)]);

const getLockedProgramDisplay = (state: StateTree, offerType: OfferType): RetainedProgramDisplay | undefined => {
    if (!explicitLenderP1AppliesToOffer(state, offerType)) return undefined;
    const retained = getPrograms(state).retainedProgramDisplay?.[offerType];
    if (retained?.program && isLockableProgram(retained.program.lenderProgram)) return withDisplayFromProgram(retained);
    const held = isProgramLookupPending(state, offerType) ? getProgramFallbackHold(state, offerType)?.lockedProgram : undefined;
    return held?.programName ? { programName: held.programName, lenderLegalName: held.lenderLegalName || '' } : undefined;
};

export const getRetainedProgramDisplay = (state: StateTree): RetainedProgramDisplay | undefined => {
    const offerType = offerRedux.getCurrentOfferType(state);

    const locked = getLockedProgramDisplay(state, offerType);
    if (locked) return locked;

    if (isRestorationEnabled(state)) {
        const programInfo = offerRedux.getPaymentProgramInfoByTerm(state, offerType);
        if (programInfo?.programName) {
            const latched = getPrograms(state).retainedProgramDisplay?.[offerType]?.program;
            return {
                programName: programInfo.programName,
                lenderLegalName: programInfo.lenderLegalName || '',
                ...(describesSameLenderProgram(latched?.lenderProgram, programInfo) && { program: latched })
            };
        }
    }

    return withDisplayFromProgram(getPrograms(state).retainedProgramDisplay?.[offerType]);
};

export const getAppliedPaymentBaseline = (state: StateTree): AppliedPaymentBaseline | undefined =>
    getPrograms(state).appliedPaymentBaseline;

export const getPendingApplyProgram = (state: StateTree): PendingApplyProgram | undefined => getPrograms(state).pendingApplyProgram;

export const isApplyingProgram = (state: StateTree): boolean => Boolean(getPendingApplyProgram(state));

export const getCreditScoreOverride = (state: StateTree): number | undefined => getPrograms(state).creditScoreOverride;

export const getAllTiersLoadedForOfferType = (state: StateTree): string | undefined =>
    getPrograms(state).allTiersLoadedForOfferType;

export const getProgramQuotesInputsKey = (state: StateTree): string => String(offerRedux.getInputAnnualMiles(state));

export const getAllTiersInputsKey = (state: StateTree): string | undefined => getPrograms(state).allTiersInputsKey;

export const isSupersededTierResponse = (state: StateTree, meta?: { requestedTier?: string; inputsKey?: string }): boolean => {
    const allTiersInputsKey = getAllTiersInputsKey(state);
    return meta?.requestedTier != null && allTiersInputsKey != null && meta.inputsKey !== allTiersInputsKey;
};

export const getPrimaryTierLoaded = (state: StateTree): boolean => Boolean(getPrograms(state).primaryTierLoaded);

export const getLoadedTierCount = (state: StateTree): number => getPrograms(state).loadedTierCount ?? 0;

export const getAllProgramsLoaded = (state: StateTree): boolean => Boolean(getPrograms(state).allProgramsLoaded);

export const getLenderProgramDisplay = (state: StateTree): string => {
    const programName = offerRedux.getSelectedTerm(state)?.lenderProgram?.programName;
    const lenderLegalName = offerRedux.getLenderLegalName(state);

    const retainedProgram = getRetainedProgramDisplay(state);
    const displayProgramName = retainedProgram?.programName || programName || '';
    const displayLenderName = retainedProgram?.lenderLegalName || lenderLegalName || '';
    return formatLenderProgramDisplay(displayLenderName, displayProgramName);
};

/**
 * Values held before a program lookup discarded the overrides, for a fallback to price with.
 *
 * Scoped to the offer type they were held for: a deal-type change holds values for the type being left,
 * which say nothing about the one being moved to.
 */
export const getProgramFallbackHold = (state: StateTree, offerType: OfferType): ProgramFallbackHold | undefined => {
    const held = getPrograms(state).programFallbackHold;
    return held && held.offerType === offerType ? held : undefined;
};

/**
 * Returns the trigger rather than a boolean because only Term, Payment Frequency, and Offer Type surface a
 * no-program message: none of them change termMonths independent of the term itself, so a no-program outcome
 * any of them triggers is still about that term. Annual Miles and other invalidating fields fall back
 * silently, since a no-program outcome they cause may be about a different matching dimension (e.g. the
 * mileage tier), not the term.
 */
export const getNoProgramLookupTrigger = (state: StateTree, offerType: OfferType): string | undefined => {
    const outcome = getPrograms(state).programLookup?.noProgram;
    if (!outcome || outcome.offerType !== offerType) return undefined;
    const currentTermMonths = offerReduxUtils.parseSelectedTermMonths(offerRedux.getTermIdByOfferType(state, offerType));
    return outcome.termMonths === currentTermMonths ? outcome.trigger : undefined;
};

export const isProgramLookupPending = (state: StateTree, offerType: OfferType): boolean =>
    getPrograms(state).programLookup?.pending?.offerType === offerType;

export const getProgramLookupPendingRequestId = (state: StateTree): number | undefined =>
    getPrograms(state).programLookup?.pending?.requestId;

export const getIsTermEstimatedNotLenderBacked = (state: StateTree): boolean => {
    if (!featureToggleSelectors.isImprovedAutoSelectEnabled(state)) return false;
    return getNoProgramLookupTrigger(state, offerRedux.getCurrentOfferType(state)) != null;
};
